import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import userProgramsService from 'services/userProgramsService';

export interface ReminderFormValues {
  title: string;
  dueDate: string;
  notes: string;
  complete?: boolean;
}

export type ReminderProgramOption = {
  programId: number;
  programName: string;
  programManagerId: number;
  currentReminders: ReminderFormValues[];
};

interface ReminderFormDropdownProps {
  onClose?: () => void;
  onSubmit?: (values: ReminderFormValues) => void | Promise<void>;
  /** Called with full list after save when using programManagerId */
  onSaved?: (list: ReminderFormValues[]) => void;
  /** Called with full list after save when using programOptions */
  onSavedForProgram?: (programId: number, list: ReminderFormValues[]) => void;
  initialValues?: Partial<ReminderFormValues>;
  /** When set, Save will PATCH api/programs-manager/:id with reminders array (same as Resume, Transcript, etc.) */
  programManagerId?: number;
  /** Current reminders list; new reminder is appended and sent in PATCH */
  currentReminders?: ReminderFormValues[];
  /**
   * When provided (and programManagerId is not set), the form will show a Program selector.
   * Save will PATCH api/programs-manager/:id for the selected program.
   */
  programOptions?: ReminderProgramOption[];
}

/** Normalize date string to yyyy-MM-dd for input type="date" */
function toDatePickerValue(value: string): string {
  if (!value?.trim()) return '';
  const d = new Date(value.trim());
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const ReminderFormDropdown: React.FC<ReminderFormDropdownProps> = ({
  onClose,
  onSubmit,
  onSaved,
  onSavedForProgram,
  initialValues,
  programManagerId,
  currentReminders = [],
  programOptions,
}) => {
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [dueDate, setDueDate] = useState(
    initialValues?.dueDate ? toDatePickerValue(initialValues.dueDate) : ''
  );
  const [notes, setNotes] = useState(initialValues?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | ''>(() => {
    const first = programOptions?.[0]?.programId;
    return typeof first === 'number' ? first : '';
  });

  const handleSave = useCallback(async () => {
    const dueDateTrim = dueDate.trim();
    if (dueDateTrim) {
      const d = new Date(dueDateTrim);
      if (isNaN(d.getTime())) {
        toast.error('Please select a valid Due Date.');
        return;
      }
    }

    const values: ReminderFormValues = {
      title: title.trim(),
      dueDate: dueDateTrim || '',
      notes: notes.trim(),
    };

    setSaving(true);
    try {
      if (programManagerId != null) {
        const newReminder = { ...values, complete: false };
        const updatedList = [...currentReminders, newReminder];
        await userProgramsService.patchProgramsManager(programManagerId, {
          reminders: updatedList.map((r) => ({
            title: r.title,
            dueDate: r.dueDate,
            notes: r.notes,
            complete: r.complete ?? false,
          })),
        });
        toast.success('Reminder saved.');
        onSaved?.(updatedList);
        onClose?.();
      } else if (programOptions?.length && typeof selectedProgramId === 'number') {
        const selected = programOptions.find((p) => p.programId === selectedProgramId);
        if (!selected) return;
        const newReminder = { ...values, complete: false };
        const updatedList = [...(selected.currentReminders || []), newReminder];
        await userProgramsService.patchProgramsManager(selected.programManagerId, {
          reminders: updatedList.map((r) => ({
            title: r.title,
            dueDate: r.dueDate,
            notes: r.notes,
            complete: r.complete ?? false,
          })),
        });
        toast.success('Reminder saved.');
        onSavedForProgram?.(selectedProgramId, updatedList);
        onClose?.();
      } else {
        if (onSubmit) await onSubmit(values);
        toast.success('Reminder saved.');
        onClose?.();
      }
    } catch {
      // Error handled by http interceptor
    } finally {
      setSaving(false);
    }
  }, [
    title,
    dueDate,
    notes,
    programManagerId,
    currentReminders,
    programOptions,
    selectedProgramId,
    onSubmit,
    onSaved,
    onSavedForProgram,
    onClose,
  ]);

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    color: '#111827',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    display: 'block' as const,
    color: '#111827',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    lineHeight: 1.5,
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 16px 16px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: '-1px',
        marginBottom: '12px',
      }}
    >
      {programManagerId == null && programOptions?.length ? (
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="reminder-program" style={labelStyle}>
            Program
          </label>
          <select
            id="reminder-program"
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(Number(e.target.value))}
            style={inputStyle}
          >
            {programOptions.map((p) => (
              <option key={p.programId} value={p.programId}>
                {p.programName}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="reminder-title" style={labelStyle}>
          Reminder title
        </label>
        <input
          id="reminder-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Reminder title..."
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="reminder-due-date" style={labelStyle}>
          Due Date
        </label>
        <input
          id="reminder-due-date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="reminder-notes" style={labelStyle}>
          Notes
        </label>
        <textarea
          id="reminder-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes..."
          rows={4}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '80px',
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#fff',
            color: '#374151',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#2563eb',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.8 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Reminder'}
        </button>
      </div>
    </div>
  );
};

export default ReminderFormDropdown;
